import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowLeft,
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  Circle,
  Layers,
  MapPin,
  Database,
  Cog,
  Download,
  RefreshCcw,
  Megaphone,
  ExternalLink,
  Loader2,
  Upload,
  FileText,
  Scale,
  Users,
  AlertTriangle,
  Eye,
  ChevronRight,
} from "lucide-react";
import CircleGlow from "../components/CircleGlow";
import RiskDiamond from "../components/RiskDiamond";
import CompactGauge from "../components/CompactGauge";
import IamaTooltip from "../components/IamaTooltip";
import ValidationDrawer from "../components/ValidationDrawer";
import {
  AuditTrailPanel,
  StepAuditList,
} from "../components/AuditTrailPanel";
import {
  findAssessment,
  setVngStep,
  loadSeedOverrides,
  loadSeedUpdates,
  loadSeedEvidence,
  loadSeedRoles,
  setStepEvidence,
  setSystemRoles,
  getSystemRoles,
  isRoleActive,
  computeReadiness,
  scoreStep,
  ROLE_KEYS,
  ROLE_LABELS,
  publishAssessment,
  getMutationLogs,
  loadSystemForEdit,
} from "../lib/assessmentStore";
import { vngSteps } from "../data/vngSteps";
import { leeuwardenSystems } from "../data/leeuwardenSystems";
import { downloadReportPdf, createAssessment } from "../lib/api";

/**
 * /system/:id  &  /platform/:id  — system detail / report page.
 * Resolves :id against (1) user-created assessments and (2) the Leeuwarden
 * baseline systems. Includes PDF export, publish-to-citizens action,
 * stats bar, board-summary block, and the interactive VNG checklist.
 */

// Sample preview URL shown for any uploaded "evidence" document. In a
// production build this would resolve to a per-file pre-signed URL served
// from the backend; for the localStorage-only mock we point every
// "Bekijk" CTA at the same officiële RiskaiΛ-sjabloon PDF.
// Until per-file pre-signed URLs are wired (P2 backlog), every simulated
// upload links its "Bekijk" CTA to the same officiële RiskaiΛ-sjabloon PDF
// so auditors can verify the full design-quality of the evidence document.
// This is the same template that the Validation Drawer exposes under
// "Optie 01 · Download het sjabloon" — closing the audit loop:
// Download → Fill → Upload → Bekijk.
const EVIDENCE_PREVIEW_URL =
  "https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/fb23c95e2a4d4c328d13aa4d02028a93_riskaia_compliance_report.pdf";

const RISK_TONE = {
  high: { color: "#F08080", icon: ShieldAlert, label: "Hoog risico" },
  medium: { color: "#F4C430", icon: ShieldAlert, label: "Midden risico" },
  low: { color: "#9FE2BF", icon: ShieldCheck, label: "Laag risico" },
};

// (Audit-trail formatting helpers moved into `components/AuditTrailPanel.jsx`
// — single source of truth for the dashboard, the per-step drawer log,
// and any future surfaces that consume mutation entries.)

const RISK_FROM_LABEL = (label = "") => {
  const v = label.toLowerCase();
  if (v.includes("hoog")) return "high";
  if (v.includes("midden") || v.includes("medium")) return "medium";
  return "low";
};

// Inverse-key map: lifecycleStage → which VNG steps are "done" up to that point.
const LWD_LIFECYCLE_PROGRESS = {
  Initiatie: ["initiatie"],
  "Data-analyse": ["initiatie", "data-analyse"],
  Ontwerp: ["initiatie", "data-analyse", "ontwerp"],
  Ontwikkeling: ["initiatie", "data-analyse", "ontwerp", "ontwikkeling"],
  Validatie: ["initiatie", "data-analyse", "ontwerp", "ontwikkeling", "validatie"],
  Implementatie: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
  ],
  Monitoring: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
    "monitoring",
  ],
  Evaluatie: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
    "monitoring",
    "evaluatie",
  ],
};

// Build a SystemResult-shaped record from a Leeuwarden seed, MERGING any
// per-step overrides the user has saved in localStorage so toggles persist
// across page refreshes.
function fromLeeuwarden(seed, seedOverrides = {}) {
  const done = LWD_LIFECYCLE_PROGRESS[seed.lifecycleStage] || [];
  const baseStatus = {};
  vngSteps.forEach((s) => {
    baseStatus[s.id] = done.includes(s.id);
  });
  const overrides = seedOverrides[seed.id] || {};
  const vngStatus = { ...baseStatus, ...overrides };
  const risk = RISK_FROM_LABEL(seed.riskClass);
  return {
    id: seed.id,
    code: seed.code,
    name: seed.name,
    org: "Gemeente Leeuwarden",
    domain: seed.tagline,
    risk,
    riskLabel: seed.riskClass,
    inzet: seed.lifecycleStage,
    regions: ["nl"],
    dataTypes: [],
    safeguards: [],
    safeguardLabels: seed.safeguards || [],
    vngStatus,
    narrative: seed.narrative,
    isSeed: true,
    published: true, // seed systems are always live in the citizen dashboard
  };
}

const SystemResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [system, setSystem] = useState(null);
  const [evidence, setEvidence] = useState({});
  const [roles, setRoles] = useState({
    projectleider: { active: false, name: "" },
    fgDpo: { active: false, name: "" },
    ethicus: { active: false, name: "" },
    auditor: { active: false, name: "" },
  });
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState(null);
  const [mutationLogs, setMutationLogs] = useState([]);
  // Stap-id of the VNG step whose Validatiepaneel is currently open. `null`
  // means no drawer is open. We keep this in state (not derived from URL)
  // because the workflow is ephemeral — the drawer state shouldn't be
  // bookmarkable.
  const [activeStepId, setActiveStepId] = useState(null);
  // Filtered slice of the audit log + human-readable summary of which
  // filters are active. The PDF exporter pipes this directly so the
  // generated dossier mirrors exactly what the auditor is looking at.
  // Audit-trail filter slice is reported up by AuditTrailPanel; retained
  // for downstream consumers (the PDF export now renders server-side).
  const [, setAuditView] = useState({ logs: null, summary: null });

  // Re-read the audit-log entries from storage. Called after each mutation
  // (toggle / note / document / role) and on initial system load.
  const refreshLogs = () => {
    if (!system) return;
    setMutationLogs(getMutationLogs(system));
  };

  useEffect(() => {
    const fromUser = findAssessment(id);
    if (fromUser) {
      setSystem(fromUser);
      setEvidence(fromUser.evidence || {});
      setRoles(getSystemRoles(fromUser));
      setMutationLogs(getMutationLogs(fromUser));
      return;
    }
    const seed = leeuwardenSystems.find((s) => s.id === id);
    if (seed) {
      const seedUpdates = loadSeedUpdates();
      const upd = seedUpdates[seed.id];
      const built = fromLeeuwarden(seed, loadSeedOverrides());
      if (upd) {
        built.lastUpdated = upd.lastUpdated;
        built.versionNote = upd.versionNote;
        built.history = upd.history || [];
      }
      setSystem(built);
      setEvidence(loadSeedEvidence()[seed.id] || {});
      setRoles(getSystemRoles(built));
      setMutationLogs(getMutationLogs(built));
      return;
    }
    navigate("/register", { replace: true });
  }, [id, navigate]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const completedCount = useMemo(
    () =>
      system ? Object.values(system.vngStatus).filter(Boolean).length : 0,
    [system]
  );

  // Phase-1 tiered scoring + dossier completeness + FG/DPO cap.
  const { pct: readinessPct, goldCount, dossierPct, capped } = useMemo(() => {
    if (!system)
      return { pct: 0, goldCount: 0, dossierPct: 0, capped: false };
    return computeReadiness({
      vngStatus: system.vngStatus,
      evidence,
      vngStepIds: vngSteps.map((s) => s.id),
      risk: system.risk,
      roles,
    });
  }, [system, evidence, roles]);

  // RiskaiΛ is een specialist voor Nederlandse gemeenten: alleen de drie
  // wettelijk verplichte kaders (EU AI Act, AVG en BIO) tellen mee voor de
  // 'Toepasselijke kaders'-tegel. Sectorale Amerikaanse normen (NIST, HIPAA,
  // SR 11-7) zijn niet van toepassing op de Leeuwarden-context. ISO/IEC 42001
  // en SOC 2 zijn aanvullend/optioneel en worden apart op /kaders gemarkeerd.
  const applicableKadersMet = 3;
  const applicableKadersTotal = 3;

  if (!system) return null;

  const tone = RISK_TONE[system.risk] || RISK_TONE.medium;
  const RiskIcon = tone.icon;

  const toggleStep = (stepId) => {
    const current = !!system.vngStatus[stepId];
    const next = !current;
    const step = vngSteps.find((s) => s.id === stepId);
    const stepLabel = step ? `${step.number} · ${step.title}` : stepId;
    setVngStep(system.id, stepId, next, !!system.isSeed, stepLabel);
    setSystem((prev) =>
      prev
        ? {
            ...prev,
            vngStatus: { ...prev.vngStatus, [stepId]: next },
          }
        : prev
    );
    refreshLogs();
  };

  // Persist evidence (note OR document filename) for a step
  const updateEvidence = (stepId, partial) => {
    const merged = {
      note: evidence[stepId]?.note || "",
      document: evidence[stepId]?.document || "",
      ...partial,
    };
    const step = vngSteps.find((s) => s.id === stepId);
    const stepLabel = step ? `${step.number} · ${step.title}` : stepId;
    setStepEvidence(system.id, stepId, merged, !!system.isSeed, stepLabel);
    setEvidence((prev) => ({ ...prev, [stepId]: merged }));
    refreshLogs();
  };

  // Roles — toggle active state OR persist the assigned functionary's name.
  // We accept the full new "{active, name}" envelope so callers can update
  // either flag without overwriting the other.
  const toggleRole = (roleKey) => {
    const current = roles[roleKey] || { active: false, name: "" };
    const next = {
      ...roles,
      [roleKey]: { ...current, active: !current.active },
    };
    setSystemRoles(system.id, next, !!system.isSeed);
    setRoles(next);
    refreshLogs();
  };

  // Local-only name change while the user types — we do NOT persist on every
  // keystroke (that would flood the audit log). The actual write + log entry
  // happens onBlur via `commitRoleName`.
  const updateRoleNameLocal = (roleKey, name) => {
    const current = roles[roleKey] || { active: false, name: "" };
    setRoles((prev) => ({
      ...prev,
      [roleKey]: { ...current, name },
    }));
  };

  // Persist the typed name + emit an audit-log entry (only if it changed).
  const commitRoleName = (roleKey) => {
    const cur = roles[roleKey] || { active: false, name: "" };
    setSystemRoles(system.id, roles, !!system.isSeed);
    refreshLogs();
    // No-op safety: if the user cleared the name to empty and the role was
    // never named before, setSystemRoles just won't emit a log entry.
    void cur;
  };

  // The compliance report PDF is produced server-side by the LaTeX EDT
  // in riskaia-reporting (GET /api/reports/{id}.pdf). This frontend never
  // generates PDFs client-side. `backendId` links the local dossier to the
  // server-persisted assessment that owns the report.
  //
  // Resolve the server-side assessment id for this system. User assessments
  // carry `backendId` from the wizard submit. Leeuwarden demo seeds (and any
  // record whose backend create failed earlier) have none, so we create one
  // on demand from the system's form snapshot — every system then yields a
  // downloadable report.
  const resolveReportId = async () => {
    if (system.backendId) return system.backendId;
    const snap = loadSystemForEdit(system.id);
    if (!snap) return null;
    const assessment = await createAssessment(snap.form);
    const newId = assessment?.id || null;
    if (newId) setSystem((prev) => (prev ? { ...prev, backendId: newId } : prev));
    return newId;
  };

  const handleDownloadPdf = async () => {
    setToast({ tone: "info", label: "Rapport wordt gegenereerd…" });
    let reportId = null;
    try {
      reportId = await resolveReportId();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[resolveReportId] failed:", err);
    }
    if (!reportId) {
      setToast({ tone: "error", label: "Rapport kon niet op de server worden aangemaakt." });
      return;
    }
    try {
      await downloadReportPdf(
        reportId,
        `riskaia-rapport-${system.code || system.id}.pdf`
      );
      setToast({ tone: "success", label: "PDF-rapport gedownload" });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[downloadReportPdf] failed:", err);
      setToast({ tone: "error", label: "PDF-rapport kon niet worden gegenereerd." });
    }
  };

  const handlePublish = () => {
    if (publishing || system.isSeed || system.published) return;
    setPublishing(true);
    setTimeout(() => {
      const updated = publishAssessment(system.id);
      if (updated) {
        setSystem((prev) => (prev ? { ...prev, ...updated } : prev));
        setToast({
          tone: "success",
          label: "Gepubliceerd naar burgers · zichtbaar in publiek overzicht",
        });
      }
      setPublishing(false);
    }, 700);
  };

  // Direct re-audit: jump straight into the 6-step wizard pre-filled with
  // the current system's data, skipping the /assessment-choice gate.
  const handleRestart = () =>
    navigate(`/wizard?edit=${encodeURIComponent(system.id)}`);

  const isPublished = !!system.published;
  const canPublish = !system.isSeed && !isPublished;

  const regionMap = {
    nl: "Nederland",
    eu: "Europese Unie",
    world: "Wereldwijd",
  };
  const headerTags = [
    system.org,
    system.domain,
    ...system.regions.map((r) => regionMap[r] || r),
  ].filter(Boolean);

  return (
    <div data-testid="page-system-result">
      {/* HEADER */}
      <section className="relative overflow-hidden">
        <CircleGlow intensity={0.7} />
        <div className="rk-container relative pt-12 md:pt-14 pb-10">
          <Link
            to="/register"
            data-testid="result-back"
            className="inline-flex items-center gap-2 text-[#D4D8DF] hover:text-white text-[12px] tracking-[0.2em] uppercase mb-8"
          >
            <ArrowLeft strokeWidth={1.25} className="w-4 h-4" />
            Terug naar register
          </Link>

          <div className="overline mb-5" data-testid="result-eyebrow">
            AI-systeem · {system.code} · {system.org}
            {system.lastUpdated && (
              <span
                className="ml-3 inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase"
                style={{
                  color: "#7FD7E4",
                  border: "1px solid rgba(91,192,235,0.35)",
                }}
                data-testid="result-version-badge"
                title={`Laatst bijgewerkt: ${new Date(
                  system.lastUpdated
                ).toLocaleString("nl-NL")}`}
              >
                Versie ·{" "}
                {new Date(system.lastUpdated).toLocaleDateString("nl-NL", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
                {system.versionNote ? ` · ${system.versionNote}` : ""}
              </span>
            )}
          </div>

          <h1
            className="text-white tracking-tight leading-[1.05] max-w-5xl text-5xl md:text-7xl"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            data-testid="result-title"
          >
            <em
              className="italic"
              style={{ fontFamily: "Fraunces, serif", color: "#FFFFFF" }}
            >
              {system.name}
            </em>
          </h1>

          {/* Tag row */}
          <div
            className="mt-6 flex flex-wrap gap-2"
            data-testid="result-header-tags"
          >
            {headerTags.map((t) => (
              <span
                key={t}
                className="text-[10px] tracking-[0.2em] uppercase border border-[#C0C5CE]/30 text-[#D4D8DF] px-3 py-1.5"
              >
                {t}
              </span>
            ))}
          </div>

          {/* Action row */}
          <div
            className="mt-8 flex flex-wrap items-center gap-3"
            data-testid="result-actions-bar"
          >
            <button
              type="button"
              onClick={handlePublish}
              disabled={!canPublish || publishing}
              className="rk-btn-primary"
              data-testid="result-cta-publish"
              data-state={
                isPublished
                  ? "published"
                  : publishing
                  ? "publishing"
                  : "idle"
              }
              style={{
                opacity: !canPublish ? 0.55 : 1,
                cursor: !canPublish ? "not-allowed" : "pointer",
              }}
            >
              {publishing ? (
                <>
                  <Loader2 strokeWidth={1.5} className="w-4 h-4 animate-spin" />
                  Publiceren...
                </>
              ) : isPublished ? (
                <>
                  <CheckCircle2 strokeWidth={1.25} className="w-4 h-4" />
                  Gepubliceerd
                </>
              ) : (
                <>
                  <Megaphone strokeWidth={1.25} className="w-4 h-4" />
                  Publiceer naar burgers
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleDownloadPdf}
              data-testid="result-cta-download-pdf"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[12px] tracking-[0.18em] uppercase bg-white text-[#0F2237] hover:bg-[#F2F4F8] transition-colors"
              style={{
                border: "1px solid rgba(15,34,55,0.15)",
              }}
            >
              <Download strokeWidth={1.5} className="w-4 h-4" />
              Download als PDF
            </button>

            <button
              type="button"
              onClick={handleRestart}
              data-testid="result-cta-restart"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[12px] tracking-[0.18em] uppercase bg-[#0A1626] text-white hover:bg-[#13243A] transition-colors"
              style={{
                border: "1px solid rgba(192,197,206,0.25)",
              }}
            >
              <RefreshCcw strokeWidth={1.25} className="w-4 h-4" />
              Start heraudit
              <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
            </button>
          </div>

          {/* Notification banner: link to public transparency page */}
          <div
            className="mt-8 border border-[#5BC0EB]/35 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            data-testid="result-public-banner"
            style={{
              background:
                "linear-gradient(120deg, rgba(91,192,235,0.08) 0%, rgba(91,192,235,0) 60%)",
            }}
          >
            <div className="flex items-start sm:items-center gap-3">
              <span
                aria-hidden
                className="inline-block w-1.5 h-1.5 rounded-full bg-[#5BC0EB] shrink-0 mt-1.5 sm:mt-0"
                style={{
                  boxShadow: "0 0 8px rgba(91,192,235,0.7)",
                  animation: "rk-pulse 2.4s ease-in-out infinite",
                }}
              />
              <div className="text-sm text-[#D4D8DF]">
                {isPublished ? (
                  <>
                    Dit systeem staat live op het publieke transparantie-portaal —
                  </>
                ) : (
                  <>
                    Klaar om te publiceren? De burgerversie van dit dossier
                    verschijnt op het publieke transparantie-portaal —
                  </>
                )}{" "}
                <Link
                  to="/publiek-overzicht"
                  className="text-[#7FD7E4] hover:text-white underline-offset-4 hover:underline inline-flex items-center gap-1"
                  data-testid="result-public-banner-link"
                >
                  bekijk publiek overzicht
                  <ExternalLink strokeWidth={1.25} className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="hairline" />
      </section>

      {/* STATS BAR */}
      <section className="rk-container py-10">
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#C0C5CE]/15"
          data-testid="result-stats-bar"
        >
          {/* Risicoklasse — flips to "Compliant" when readiness reaches 100% */}
          {(() => {
            const isCompliant = readinessPct >= 100;
            const statusLevel = isCompliant ? "compliant" : system.risk;
            const statusColor = isCompliant ? "#9FE2BF" : tone.color;
            const statusLabel = isCompliant ? "Compliant" : tone.label;
            return (
              <div
                className="bg-[#162E4A] p-7 flex items-center gap-5"
                data-testid="result-stat-risk"
                data-status={isCompliant ? "compliant" : "risk"}
              >
                <RiskDiamond level={statusLevel} size={64} />
                <div>
                  <div className="overline mb-1.5">
                    {isCompliant ? "Status" : "Risicoklasse"}
                  </div>
                  <div
                    className="text-2xl"
                    style={{
                      fontFamily: "Fraunces, serif",
                      fontWeight: 400,
                      color: statusColor,
                    }}
                  >
                    {statusLabel}
                  </div>
                  <div className="text-[11px] tracking-[0.16em] uppercase text-[#9AA4B2] mt-1">
                    {isCompliant
                      ? `Volledig conform · ${tone.label} (Annex III)`
                      : "Conform AI Act Annex III"}
                  </div>
                  {system.risk === "high" && !isCompliant && (
                    <IamaTooltip>
                      <span
                        className="inline-flex items-center gap-1.5 mt-2 px-2 py-1 text-[10px] tracking-[0.22em] uppercase cursor-help"
                        data-testid="result-stat-risk-iama"
                        style={{
                          color: "#F4C430",
                          border: "1px solid rgba(244,196,48,0.45)",
                          background: "rgba(244,196,48,0.08)",
                        }}
                      >
                        <AlertTriangle
                          strokeWidth={1.5}
                          className="w-3 h-3"
                        />
                        IAMA verplicht
                      </span>
                    </IamaTooltip>
                  )}
                  {system.risk === "high" && isCompliant && (
                    <IamaTooltip>
                      <span
                        className="inline-flex items-center gap-1.5 mt-2 px-2 py-1 text-[10px] tracking-[0.22em] uppercase cursor-help"
                        data-testid="result-stat-risk-iama-met"
                        style={{
                          color: "#9FE2BF",
                          border: "1px solid rgba(159,226,191,0.45)",
                          background: "rgba(159,226,191,0.08)",
                        }}
                      >
                        <CheckCircle2
                          strokeWidth={1.5}
                          className="w-3 h-3"
                        />
                        IAMA voldaan
                      </span>
                    </IamaTooltip>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Gereedheid */}
          <div
            className="bg-[#162E4A] p-7 flex items-center gap-5"
            data-testid="result-stat-readiness"
          >
            <CompactGauge value={readinessPct} size={88} />
            <div>
              <div className="overline mb-1.5">Gereedheid</div>
              <div
                className="text-2xl text-white flex items-baseline gap-2"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 400 }}
              >
                {readinessPct}%
                {capped && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase"
                    style={{
                      color: "#F4C430",
                      fontFamily: "Outfit, sans-serif",
                    }}
                    title="Geblokkeerd op 95%: FG/DPO ontbreekt voor hoog risico"
                    data-testid="result-readiness-capped"
                  >
                    <AlertTriangle
                      strokeWidth={1.5}
                      className="w-3 h-3"
                    />
                    Cap 95%
                  </span>
                )}
              </div>
              <div className="text-[11px] tracking-[0.16em] uppercase text-[#9AA4B2] mt-1">
                {goldCount} goud · {completedCount} / {vngSteps.length} stappen
              </div>
            </div>
          </div>

          {/* Toepasselijke kaders */}
          <div
            className="bg-[#162E4A] p-7 flex items-center gap-5"
            data-testid="result-stat-kaders"
          >
            <div
              className="w-16 h-16 border border-[#C0C5CE]/40 flex items-center justify-center shrink-0"
              aria-hidden
            >
              <Layers
                strokeWidth={1}
                className="w-7 h-7 text-[#C0C5CE]"
              />
            </div>
            <div>
              <div className="overline mb-1.5">Toepasselijke kaders</div>
              <div
                className="text-2xl text-white"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 400 }}
              >
                {applicableKadersMet} van {applicableKadersTotal} kaders
              </div>
              <div className="text-[11px] tracking-[0.16em] uppercase text-[#9AA4B2] mt-1">
                Wettelijk verplicht (AI Act, AVG, BIO)
              </div>
              <Link
                to="/kaders"
                className="inline-flex items-center gap-1 text-[11px] tracking-[0.16em] uppercase text-[#7FD7E4] hover:text-white mt-2"
                data-testid="result-stat-kaders-link"
              >
                Bekijk kaders
                <ArrowUpRight strokeWidth={1.25} className="w-3 h-3" />
              </Link>
            </div>
          </div>

          {/* Dossier Volledigheid */}
          <div
            className="bg-[#162E4A] p-7 flex items-center gap-5"
            data-testid="result-stat-dossier"
          >
            <CompactGauge value={dossierPct} size={88} />
            <div>
              <div className="overline mb-1.5">Dossier volledigheid</div>
              <div
                className="text-2xl text-white"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 400 }}
              >
                {dossierPct}%
              </div>
              <div className="text-[11px] tracking-[0.16em] uppercase text-[#9AA4B2] mt-1">
                {goldCount} stappen met bewijs
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECTTEAM & ROLLEN */}
      <section
        className="rk-container pb-10"
        data-testid="result-roles-section"
      >
        <div
          className="border border-[#C0C5CE]/25 p-7 md:p-9"
          style={{ background: "#162E4A" }}
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-7">
            <div>
              <div
                className="overline mb-3 flex items-center gap-2"
                data-testid="result-roles-eyebrow"
              >
                <Users strokeWidth={1.25} className="w-4 h-4" />
                Projectteam &amp; rollen
              </div>
              <h2
                className="text-2xl md:text-3xl text-white tracking-tight"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
              >
                Wie zorgt voor de{" "}
                <em
                  className="italic text-[#D4D8DF]"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  checks &amp; balances?
                </em>
              </h2>
              <p className="mt-3 text-sm text-[#9AA4B2] max-w-2xl leading-relaxed">
                Vier kernrollen waarborgen onafhankelijk toezicht. Voor
                hoog-risico systemen blokkeert RiskaiΛ de readiness op 95% als
                de FG/DPO ontbreekt.
              </p>
            </div>
            {system.risk === "high" && (
              <IamaTooltip
                title="Functionaris Gegevensbescherming (FG / DPO)"
                body="De Functionaris Gegevensbescherming (FG) — internationaal bekend als de Data Protection Officer (DPO) — is de onafhankelijke interne toezichthouder binnen uw organisatie. De inzet van een FG is wettelijk verplicht voor overheden die persoonsgegevens verwerken. Als 'het geweten' van de organisatie toetst deze rol of AI-systemen voldoen aan de AVG en fundamentele burgerrechten. RiskaiΛ dwingt de betrokkenheid van de FG/DPO af voor hoog-risico systemen: zonder hun onafhankelijke controle is een 100% gereedheids-score juridisch en ethisch onmogelijk."
              >
                <span
                  className="text-[11px] tracking-[0.18em] uppercase shrink-0 px-3 py-1.5 cursor-help"
                  data-testid="result-roles-fgdpo-badge"
                  style={{
                    color: "#F4C430",
                    border: "1px solid rgba(244,196,48,0.4)",
                    background: "rgba(244,196,48,0.07)",
                  }}
                >
                  Hoog-risico · FG/DPO verplicht
                </span>
              </IamaTooltip>
            )}
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
            data-testid="result-roles-grid"
          >
            {ROLE_KEYS.map((rk) => {
              const entry = roles[rk] || { active: false, name: "" };
              const active = !!entry.active;
              const isFgDpo = rk === "fgDpo";
              const blocking = isFgDpo && system.risk === "high" && !active;
              return (
                <div
                  key={rk}
                  data-testid={`result-role-${rk}`}
                  data-active={active ? "true" : "false"}
                  className="p-4 transition-colors duration-300"
                  style={{
                    background: active
                      ? "rgba(159,226,191,0.08)"
                      : "transparent",
                    border: `1px solid ${
                      active
                        ? "rgba(159,226,191,0.55)"
                        : blocking
                        ? "rgba(244,196,48,0.45)"
                        : "rgba(192,197,206,0.25)"
                    }`,
                  }}
                >
                  {/* Click region — the role TOGGLE only */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-pressed={active}
                    onClick={() => toggleRole(rk)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleRole(rk);
                      }
                    }}
                    data-testid={`result-role-toggle-${rk}`}
                    style={{ cursor: "pointer", outline: "none" }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="overline"
                        style={{
                          color: active
                            ? "#9FE2BF"
                            : blocking
                            ? "#F4C430"
                            : "#9AA4B2",
                        }}
                      >
                        {active ? "Aangesteld" : "Niet aangesteld"}
                      </span>
                      {active ? (
                        <CheckCircle2
                          strokeWidth={1.5}
                          className="w-4 h-4"
                          style={{ color: "#9FE2BF" }}
                        />
                      ) : (
                        <Circle
                          strokeWidth={1}
                          className="w-4 h-4 text-[#9AA4B2]"
                        />
                      )}
                    </div>
                    <div
                      className="text-lg text-white"
                      style={{
                        fontFamily: "Fraunces, serif",
                        fontWeight: 400,
                      }}
                    >
                      {ROLE_LABELS[rk]}
                    </div>
                    {blocking && (
                      <div
                        className="mt-1 text-[10px] tracking-[0.14em] uppercase"
                        style={{ color: "#F4C430" }}
                      >
                        Blokkeert readiness &gt; 95%
                      </div>
                    )}
                  </div>

                  {/* Naam-functionaris input — visible only when role active */}
                  {active && (
                    <div className="mt-3">
                      <label
                        className="block text-[10px] tracking-[0.22em] uppercase mb-1.5"
                        style={{ color: "#7FD7E4" }}
                        htmlFor={`role-name-${rk}`}
                      >
                        Naam functionaris
                      </label>
                      <input
                        id={`role-name-${rk}`}
                        type="text"
                        value={entry.name || ""}
                        onChange={(e) =>
                          updateRoleNameLocal(rk, e.target.value)
                        }
                        onBlur={() => commitRoleName(rk)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.currentTarget.blur();
                          }
                        }}
                        placeholder="Naam functionaris..."
                        data-testid={`result-role-name-${rk}`}
                        className="w-full bg-transparent text-white px-2 py-1.5"
                        style={{
                          fontFamily:
                            "ui-monospace, 'JetBrains Mono', 'Menlo', monospace",
                          fontSize: "12.5px",
                          letterSpacing: "0.02em",
                          border:
                            "1px solid rgba(127,215,228,0.30)",
                          outline: "none",
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SAMENVATTING VOOR HET BESTUUR */}
      <section className="rk-container pb-10" data-testid="result-summary-board">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-4">
              Samenvatting voor het bestuur
            </div>
            <h2
              className="text-3xl md:text-4xl text-white tracking-tight leading-tight"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            >
              Wat dit systeem
              <br />
              <em
                className="italic text-[#D4D8DF]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                betekent voor uw burgers.
              </em>
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p
              className="text-lg text-[#D4D8DF] leading-relaxed"
              data-testid="result-narrative"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            >
              {system.narrative}
            </p>

            {/* Profile + safeguards */}
            <div className="hairline my-8" />
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-8 text-sm">
              <Meta
                icon={Layers}
                label="Domein"
                value={system.domain}
                testid="meta-domain"
              />
              <Meta
                icon={Cog}
                label="Inzet"
                value={system.inzet}
                testid="meta-inzet"
              />
              <Meta
                icon={MapPin}
                label="Regio's"
                value={
                  system.regions.length === 0
                    ? "—"
                    : system.regions.map((r) => regionMap[r] || r).join(" · ")
                }
                testid="meta-regions"
              />
              <Meta
                icon={Database}
                label="Datatypen"
                value={
                  system.dataTypes.length === 0
                    ? "—"
                    : `${system.dataTypes.length} geselecteerd`
                }
                testid="meta-datatypes"
              />
            </dl>
            {system.safeguardLabels.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {system.safeguardLabels.map((s) => (
                  <span
                    key={s}
                    className="rk-chip"
                    data-testid="result-safeguard"
                  >
                    <ShieldCheck strokeWidth={1.25} className="w-3.5 h-3.5" />{" "}
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="hairline" />

      {/* VNG CHECKLIST · with juridical ties + evidence */}
      <section className="rk-container py-20" data-testid="result-vng">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <div className="overline mb-4">VNG-lifecycle · juridisch verankerd</div>
            <h2
              className="text-3xl md:text-4xl text-white tracking-tight leading-tight"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            >
              De acht stappen
              <br />
              <span
                className="italic text-[#D4D8DF]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                voor dit systeem.
              </span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-[#D4D8DF] leading-relaxed">
              <span className="text-white">{completedCount}</span> van 8
              stappen bevestigd · <span className="text-white">{goldCount}</span>{" "}
              met administratieve onderbouwing.{" "}
              <span className="text-[#5BC0EB]">Bevestig</span> een stap, schrijf
              een onderbouwing of upload een document — pas dan telt de stap
              voor 100%.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 text-[10px] tracking-[0.2em] uppercase">
              <span
                className="px-2 py-1"
                style={{
                  border: "1px solid rgba(159,226,191,0.5)",
                  color: "#9FE2BF",
                  background: "rgba(159,226,191,0.06)",
                }}
              >
                Goud · met bewijs · 100%
              </span>
              <span
                className="px-2 py-1"
                style={{
                  border: "1px solid rgba(192,197,206,0.4)",
                  color: "#D4D8DF",
                }}
              >
                Zilver · alleen bevestigd · 50%
              </span>
              <span
                className="px-2 py-1"
                style={{
                  border: "1px solid rgba(154,164,178,0.35)",
                  color: "#9AA4B2",
                }}
              >
                Open · 0%
              </span>
            </div>
          </div>
        </div>

        <ol
          className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#C0C5CE]/15"
          data-testid="result-vng-grid"
        >
          {vngSteps.map((s) => {
            const ok = !!system.vngStatus[s.id];
            const ev = evidence[s.id] || { note: "", document: "" };
            const tier = scoreStep(ok, ev).tier; // gold | silver | open
            const Icon = s.icon;
            const tierColor =
              tier === "gold"
                ? "#9FE2BF"
                : tier === "silver"
                ? "#D4D8DF"
                : "#9AA4B2";
            return (
              <li
                key={s.id}
                data-testid={`vng-status-${s.id}`}
                data-ok={ok}
                data-tier={tier}
                className="bg-[#162E4A] p-6 md:p-7 flex flex-col gap-5"
              >
                {/* Header row */}
                <div className="flex items-start gap-5">
                  <button
                    type="button"
                    onClick={() => toggleStep(s.id)}
                    data-testid={`vng-toggle-${s.id}`}
                    aria-pressed={ok}
                    aria-label={`Bevestig ${s.title}`}
                    className="w-10 h-10 border flex items-center justify-center shrink-0 transition-all duration-300"
                    style={{
                      borderColor:
                        tier === "gold"
                          ? "rgba(159,226,191,0.85)"
                          : ok
                          ? "rgba(192,197,206,0.55)"
                          : "rgba(192,197,206,0.3)",
                      background:
                        tier === "gold"
                          ? "rgba(159, 226, 191, 0.1)"
                          : "transparent",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    {ok ? (
                      <CheckCircle2
                        strokeWidth={1.5}
                        className="w-5 h-5"
                        style={{ color: tierColor }}
                      />
                    ) : (
                      <Circle
                        strokeWidth={1}
                        className="w-5 h-5 text-[#9AA4B2]"
                      />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span
                        className="overline"
                        style={{ color: tierColor }}
                      >
                        Stap {s.number}
                      </span>
                      <Icon
                        strokeWidth={1}
                        className="w-3.5 h-3.5"
                        style={{ color: tierColor }}
                      />
                      {/* Legal tag — EU AI Act article */}
                      {s.legal && (
                        <span
                          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5"
                          style={{
                            border: "1px solid rgba(127,215,228,0.45)",
                            color: "#7FD7E4",
                            background: "rgba(91,192,235,0.06)",
                          }}
                          data-testid={`vng-legal-${s.id}`}
                        >
                          <Scale strokeWidth={1.25} className="w-3 h-3" />
                          EU AI Act · {s.legal.article} — {s.legal.topic}
                        </span>
                      )}
                      {/* IAMA mandate badge — only on Ontwerp step for high-risk systems */}
                      {s.id === "ontwerp" && system.risk === "high" && (
                        <IamaTooltip>
                          <span
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase px-2 py-0.5 cursor-help"
                            data-testid={`vng-iama-${s.id}`}
                            style={{
                              border: "1px solid rgba(244,196,48,0.55)",
                              color: "#F4C430",
                              background: "rgba(244,196,48,0.10)",
                            }}
                          >
                            <AlertTriangle strokeWidth={1.5} className="w-3 h-3" />
                            Wettelijke IAMA-plicht
                          </span>
                        </IamaTooltip>
                      )}
                    </div>
                    <h3
                      className="text-xl text-white"
                      style={{ fontFamily: "Fraunces, serif", fontWeight: 400 }}
                    >
                      {s.title}
                      {s.id === "ontwerp" && (
                        <IamaTooltip>
                          <span
                            className="block text-sm mt-1 italic cursor-help"
                            style={{
                              color: "#7FD7E4",
                              fontFamily: "Fraunces, serif",
                              fontWeight: 300,
                            }}
                            data-testid="vng-ontwerp-iama-label"
                          >
                            Mensenrechten (IAMA)
                          </span>
                        </IamaTooltip>
                      )}
                    </h3>
                    <p
                      className="text-sm mt-1"
                      style={{ color: tierColor }}
                    >
                      {tier === "gold"
                        ? "Goud · bevestigd én onderbouwd."
                        : tier === "silver"
                        ? "Zilver · bevestigd, onderbouwing ontbreekt."
                        : "Open · wacht op bevestiging en onderbouwing"}
                    </p>
                  </div>
                </div>

                {/* Compact validation summary + drawer trigger */}
                <div
                  className="flex items-center justify-between gap-4 pl-[60px]"
                  data-testid={`vng-evidence-${s.id}`}
                >
                  <div className="flex flex-col gap-1 min-w-0">
                    <span
                      className="text-[10px] tracking-[0.22em] uppercase"
                      style={{ color: tierColor }}
                    >
                      {tier === "gold"
                        ? "Volledig dossier"
                        : tier === "silver"
                        ? "Onderbouwing vereist"
                        : "Nog niet gevalideerd"}
                    </span>
                    {ev.document ? (
                      <span
                        className="text-xs truncate"
                        style={{ color: "#D4D8DF" }}
                        title={ev.document}
                      >
                        <FileText
                          strokeWidth={1.25}
                          className="inline w-3 h-3 mr-1.5 align-[-2px]"
                          style={{ color: tierColor }}
                        />
                        {ev.document}
                      </span>
                    ) : ev.note ? (
                      <span
                        className="text-xs italic truncate"
                        style={{ color: "#9AA4B2" }}
                      >
                        Onderbouwing toegevoegd · document ontbreekt
                      </span>
                    ) : (
                      <span
                        className="text-xs italic"
                        style={{ color: "#9AA4B2" }}
                      >
                        Open het validatiepaneel om bewijs toe te voegen
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveStepId(s.id)}
                    data-testid={`vng-open-drawer-${s.id}`}
                    className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase px-3.5 py-2 shrink-0 transition-colors"
                    style={{
                      border: "1px solid rgba(127,215,228,0.45)",
                      color: "#7FD7E4",
                      background: "rgba(91,192,235,0.06)",
                      cursor: "pointer",
                    }}
                  >
                    Open validatiepaneel
                    <ChevronRight strokeWidth={1.5} className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      {/* CTAs */}
      <section className="rk-container pb-24">
        <div
          className="border border-[#C0C5CE]/30 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-12"
          data-testid="result-actions"
        >
          <div className="flex-1">
            <div className="overline mb-3">Wat nu?</div>
            <h3
              className="text-2xl md:text-3xl text-white tracking-tight"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            >
              {system.name} is geregistreerd.
            </h3>
            <p className="mt-3 text-[#D4D8DF] leading-relaxed max-w-2xl">
              U kunt het systeem nu publiek inzien in het register, of
              doorklikken naar de Cockpit om de waarborgen verder te beheren.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <Link
              to="/register"
              className="rk-btn-primary"
              data-testid="result-cta-register"
            >
              Bekijk in Register
              <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
            </Link>
            <Link
              to="/platform"
              className="rk-btn-cyan"
              data-testid="result-cta-cockpit"
            >
              Ga naar Cockpit
              <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          MUTATION HISTORY (AUDIT TRAIL)
          ============================================================ */}
      <AuditTrailPanel
        logs={mutationLogs}
        onFilteredChange={setAuditView}
      />

      {/* ============================================================
          VALIDATION DRAWER — Zijscherm voor de actieve VNG-stap
          ============================================================ */}
      {(() => {
        const activeStep = activeStepId
          ? vngSteps.find((vs) => vs.id === activeStepId)
          : null;
        const ev = activeStep
          ? evidence[activeStep.id] || { note: "", document: "" }
          : { note: "", document: "" };
        return (
          <ValidationDrawer
            open={!!activeStep}
            onClose={() => setActiveStepId(null)}
            step={activeStep}
            isHighRisk={system.risk === "high"}
            onDownloadTemplate={() => {
              // The official sjabloon is filled in on its own form page; the
              // PDF is generated there (HTML form -> sjabloon.tex EDT, with a
              // real /Sig signature via xdp-sign).
              navigate(`/sjabloon/${encodeURIComponent(system.id)}`);
            }}
          >
            {activeStep && (
              <div className="space-y-4">
                <SimulatedUploadButton
                  stepId={activeStep.id}
                  currentDocument={ev.document}
                  onUploaded={(filename) =>
                    updateEvidence(activeStep.id, { document: filename })
                  }
                  onCleared={() =>
                    updateEvidence(activeStep.id, { document: "" })
                  }
                />

                <div>
                  <label
                    htmlFor={`drawer-note-${activeStep.id}`}
                    className="overline block mb-2"
                    style={{ color: "#9AA4B2" }}
                  >
                    Administratieve onderbouwing
                  </label>
                  <textarea
                    id={`drawer-note-${activeStep.id}`}
                    rows={6}
                    value={ev.note}
                    onChange={(e) =>
                      updateEvidence(activeStep.id, { note: e.target.value })
                    }
                    placeholder={
                      activeStep.placeholder ||
                      "Bijv.: getekend besluit B&W d.d. 12-03-2026, IAMA-rapportage v2.1…"
                    }
                    data-testid={`drawer-note-${activeStep.id}`}
                    className="w-full text-sm leading-relaxed bg-transparent text-white placeholder:text-[#9AA4B2]"
                    style={{
                      border: "1px solid rgba(192,197,206,0.25)",
                      padding: "0.7rem 0.9rem",
                      outline: "none",
                      resize: "vertical",
                      fontFamily: "Outfit, sans-serif",
                    }}
                  />
                </div>

                {/* Bevestig-stap checkbox so the drawer is self-contained */}
                <button
                  type="button"
                  onClick={() => toggleStep(activeStep.id)}
                  data-testid={`drawer-toggle-${activeStep.id}`}
                  className="w-full inline-flex items-center justify-between gap-3 px-4 py-3 transition-colors"
                  style={{
                    border: !!system.vngStatus[activeStep.id]
                      ? "1px solid rgba(159,226,191,0.55)"
                      : "1px solid rgba(192,197,206,0.30)",
                    background: !!system.vngStatus[activeStep.id]
                      ? "rgba(159,226,191,0.08)"
                      : "transparent",
                    cursor: "pointer",
                    color: !!system.vngStatus[activeStep.id]
                      ? "#9FE2BF"
                      : "#D4D8DF",
                  }}
                >
                  <span className="text-[11px] tracking-[0.22em] uppercase">
                    {!!system.vngStatus[activeStep.id]
                      ? "Stap bevestigd"
                      : "Bevestig deze stap"}
                  </span>
                  {!!system.vngStatus[activeStep.id] ? (
                    <CheckCircle2 strokeWidth={1.5} className="w-4 h-4" />
                  ) : (
                    <Circle strokeWidth={1.25} className="w-4 h-4" />
                  )}
                </button>

                {/* Step-specific audit log */}
                <div className="pt-2">
                  <StepAuditList
                    logs={mutationLogs}
                    stepNumber={activeStep.number}
                  />
                </div>
              </div>
            )}
          </ValidationDrawer>
        );
      })()}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          data-testid="result-toast"
          className="fixed bottom-8 right-8 z-50 rk-fade-up"
          style={{
            background: "#162E4A",
            border: "1px solid rgba(159,226,191,0.55)",
            padding: "0.85rem 1.1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.7rem",
            boxShadow: "0 24px 48px -16px rgba(0,0,0,0.6)",
            maxWidth: "min(94vw, 420px)",
          }}
        >
          <CheckCircle2
            strokeWidth={1.5}
            className="w-5 h-5"
            style={{ color: "#9FE2BF" }}
          />
          <span className="text-sm text-white">{toast.label}</span>
        </div>
      )}
    </div>
  );
};

const Meta = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid}>
    <dt className="overline mb-1.5 flex items-center gap-2">
      <Icon strokeWidth={1} className="w-3.5 h-3.5" /> {label}
    </dt>
    <dd className="text-white">{value}</dd>
  </div>
);

// Simulated upload — opens the OS file dialog, stores ONLY the filename in
// localStorage. No bytes leave the browser. Used as proof-of-evidence in
// the audit dossier.
const SimulatedUploadButton = ({
  stepId,
  currentDocument,
  onUploaded,
  onCleared,
}) => {
  const inputId = `upload-${stepId}`;
  const has = !!currentDocument;
  return (
    <div className="flex items-center gap-2">
      <input
        id={inputId}
        type="file"
        className="sr-only"
        data-testid={`vng-upload-input-${stepId}`}
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.csv,.xlsx"
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (f) onUploaded(f.name);
          // Reset so the same file can be re-picked later
          e.target.value = "";
        }}
      />
      <label
        htmlFor={inputId}
        data-testid={`vng-upload-${stepId}`}
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase px-3 py-2 cursor-pointer transition-colors"
        style={{
          border: has
            ? "1px solid rgba(159,226,191,0.55)"
            : "1px solid rgba(192,197,206,0.35)",
          color: has ? "#9FE2BF" : "#D4D8DF",
          background: has ? "rgba(159,226,191,0.06)" : "transparent",
        }}
      >
        {has ? (
          <FileText strokeWidth={1.25} className="w-3.5 h-3.5" />
        ) : (
          <Upload strokeWidth={1.25} className="w-3.5 h-3.5" />
        )}
        {has ? "Document gekoppeld" : "Document uploaden"}
      </label>
      {has && (
        <span
          className="text-[11px] truncate max-w-[180px]"
          style={{ color: "#9AA4B2", fontFamily: "Outfit, sans-serif" }}
          title={currentDocument}
        >
          {currentDocument}
        </span>
      )}
      {has && (
        <a
          href={EVIDENCE_PREVIEW_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`vng-upload-view-${stepId}`}
          aria-label={`Bekijk gekoppeld document ${currentDocument}`}
          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase transition-colors"
          style={{
            color: "#7FD7E4",
            textDecoration: "none",
            borderBottom: "1px solid transparent",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#A4E5EE";
            e.currentTarget.style.borderBottom =
              "1px solid rgba(164,229,238,0.65)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "#7FD7E4";
            e.currentTarget.style.borderBottom = "1px solid transparent";
          }}
        >
          <Eye strokeWidth={1.25} className="w-3.5 h-3.5" />
          Bekijk
        </a>
      )}
      {has && (
        <button
          type="button"
          onClick={onCleared}
          data-testid={`vng-upload-clear-${stepId}`}
          aria-label="Verwijder gekoppeld document"
          className="text-[10px] tracking-[0.18em] uppercase"
          style={{ color: "#F08080", border: "none", background: "transparent" }}
        >
          Verwijder
        </button>
      )}
    </div>
  );
};

export default SystemResult;
